/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/shellAssistantLayout.ts
 * PURPOSE: Layout constants for split assistant frame + bottom bar [2.2.1341].
 * PUBLIC_API: SHELL_ASSISTANT_BOTTOM_BAR_HEIGHT_PX, dock height helpers
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-09 [2.2.1361] — Mobile reserved chrome: assistant-split excludes global bottom nav; named layout constants.
 * RECENT_PRIOR: 2026-06-09 [2.2.1360] — Default desktop dock height **560px** (was 480) for more chat area.
 */

/** Height of the Page | Assistant toggle bar (px), excluding safe-area inset. */
export const SHELL_ASSISTANT_BOTTOM_BAR_HEIGHT_PX = 36;

/** `ShellAssistantBottomBar` minHeight + `py-1` — excludes safe-area padding. */
export const SHELL_ASSISTANT_BOTTOM_BAR_CHROME_PX = SHELL_ASSISTANT_BOTTOM_BAR_HEIGHT_PX + 8;

/** `MobileBottomNav` list row (`h-16`). */
export const MOBILE_BOTTOM_NAV_HEIGHT_PX = 64;

/** Mobile `TopBar` approximate chrome (icon row + border). */
export const MOBILE_SHELL_TOP_CHROME_PX = 56;

/** `AIAssistantDock` resize handle (`h-3`). */
export const SHELL_ASSISTANT_DOCK_RESIZE_HANDLE_PX = 12;

/** `AIAssistantRightRail` left-edge resize handle (`w-1.5`). */
export const SHELL_ASSISTANT_RAIL_RESIZE_HANDLE_PX = 6;

export const SHELL_ASSISTANT_DOCK_HEIGHT_STORAGE_KEY = 'brazn_assistant_dock_height_px';
export const SHELL_ASSISTANT_RAIL_WIDTH_STORAGE_KEY = 'brazn_assistant_rail_width_px';

const DOCK_MIN_PX = 260;
const DOCK_MAX_PX_DESKTOP = 720;
const DOCK_MAX_PX_MOBILE = 480;
const DOCK_DEFAULT_DESKTOP = 560;
const DOCK_DEFAULT_MOBILE = 300;

/** Which bottom chrome stacks with the dock on mobile shell. */
export type MobileShellChromeMode = 'assistant-split' | 'page-with-nav';

/**
 * Reserved vertical chrome (px) for dock height math on mobile shell.
 * Assistant split hides global bottom nav — only Page | Assistant bar stacks below the dock.
 */
export function mobileShellReservedChromePx(mode: MobileShellChromeMode): number {
  const topAndResize = MOBILE_SHELL_TOP_CHROME_PX + SHELL_ASSISTANT_DOCK_RESIZE_HANDLE_PX;
  return mode === 'assistant-split'
    ? topAndResize + SHELL_ASSISTANT_BOTTOM_BAR_CHROME_PX
    : topAndResize + MOBILE_BOTTOM_NAV_HEIGHT_PX;
}

/** Static ceiling before viewport-aware clamping [2.2.1354]. */
export function staticShellAssistantDockMaxHeightPx(isMobile: boolean): number {
  return isMobile ? DOCK_MAX_PX_MOBILE : DOCK_MAX_PX_DESKTOP;
}

/**
 * Cap dock height so split view leaves readable main content on phones [2.2.1354].
 * Mobile defaults to assistant-split (global bottom nav hidden while dock is open).
 */
export function maxShellAssistantDockHeightPx(
  isMobile: boolean,
  viewportHeight = 800,
  mobileChromeMode: MobileShellChromeMode = 'assistant-split'
): number {
  const staticMax = staticShellAssistantDockMaxHeightPx(isMobile);
  const reservedChrome = isMobile
    ? mobileShellReservedChromePx(mobileChromeMode)
    : SHELL_ASSISTANT_BOTTOM_BAR_CHROME_PX + MOBILE_SHELL_TOP_CHROME_PX + 48;
  const minMain = isMobile ? 160 : 200;
  const dynamicMax = viewportHeight - reservedChrome - minMain;
  return Math.min(staticMax, Math.max(DOCK_MIN_PX, Math.round(dynamicMax)));
}

export function defaultShellAssistantDockHeightPx(
  isMobile: boolean,
  viewportHeight = 800,
  mobileChromeMode: MobileShellChromeMode = 'assistant-split'
): number {
  const preferred = isMobile ? DOCK_DEFAULT_MOBILE : DOCK_DEFAULT_DESKTOP;
  return Math.min(preferred, maxShellAssistantDockHeightPx(isMobile, viewportHeight, mobileChromeMode));
}

export function clampShellAssistantDockHeightPx(
  px: number,
  isMobile: boolean,
  viewportHeight = 800,
  mobileChromeMode: MobileShellChromeMode = 'assistant-split'
): number {
  const max = maxShellAssistantDockHeightPx(isMobile, viewportHeight, mobileChromeMode);
  return Math.min(max, Math.max(DOCK_MIN_PX, Math.round(px)));
}

/** @deprecated Use defaultShellAssistantDockHeightPx — kept for tests referencing vh helper */
export function shellAssistantDockInlineHeight(isMobile: boolean): string {
  const px = defaultShellAssistantDockHeightPx(isMobile);
  return `${px}px`;
}

const RAIL_MIN_PX = 320;
const RAIL_MAX_PX_DESKTOP = 560;
const RAIL_MAX_PX_MOBILE = 480;
const RAIL_DEFAULT_DESKTOP = 420;
const RAIL_DEFAULT_MOBILE = 420;

export function staticShellAssistantRailMaxWidthPx(isMobile: boolean): number {
  return isMobile ? RAIL_MAX_PX_MOBILE : RAIL_MAX_PX_DESKTOP;
}

export function maxShellAssistantRailWidthPx(
  isMobile: boolean,
  viewportWidth = 1280
): number {
  const staticMax = staticShellAssistantRailMaxWidthPx(isMobile);
  const minMain = isMobile ? 0 : 360;
  const dynamicMax = viewportWidth - minMain;
  return Math.min(staticMax, Math.max(RAIL_MIN_PX, Math.round(dynamicMax)));
}

export function defaultShellAssistantRailWidthPx(
  isMobile: boolean,
  viewportWidth = 1280
): number {
  const preferred = isMobile ? RAIL_DEFAULT_MOBILE : RAIL_DEFAULT_DESKTOP;
  return Math.min(preferred, maxShellAssistantRailWidthPx(isMobile, viewportWidth));
}

export function clampShellAssistantRailWidthPx(
  px: number,
  isMobile: boolean,
  viewportWidth = 1280
): number {
  const max = maxShellAssistantRailWidthPx(isMobile, viewportWidth);
  return Math.min(max, Math.max(RAIL_MIN_PX, Math.round(px)));
}
